import { useState } from "react";
import { Table, App } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FundProjectionScreenOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Monitor, MonitorColorBy, MonitorView } from "../../../../api/monitors";
import type { UserListItemOut } from "../../../../api/auth";
import type { Station } from "../../../../api/stations";
import { getTranslation } from "../../../../utils/translations";
import {
  useUpdateMonitor,
  useDeleteMonitor,
} from "../../../../hooks/useMonitors";
import MonitorFormModal, { type MonitorFormValues } from "./MonitorFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";
import { useTranslation } from "react-i18next";
import {
  ConfigPanel,
  PanelAddButton,
  PanelIconAction,
} from "../panel/ConfigPanel";
import { PANEL_ACCENTS, type PanelAccent } from "../panel/panelAccents";

const R = {
  bg: "var(--color-canvas)",
  border: "var(--color-shade-2)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
};

interface Props {
  monitors: Monitor[];
  users: UserListItemOut[];
  stations: Station[];
  // Коя таблица е това: обикновени монитори или шпикери. Определя текстовете,
  // бутона за добавяне и кои потребители се предлагат при редакция.
  variant: "monitor" | "shpicker";
  isNarrow?: boolean;
  onAddMonitor?: () => void;
}

const MonitorsTable = ({
  monitors,
  users,
  stations,
  variant,
  isNarrow = false,
  onAddMonitor,
}: Props) => {
  const { t } = useTranslation();
  const updateMonitorMutation = useUpdateMonitor();
  const deleteMonitorMutation = useDeleteMonitor();
  const { message } = App.useApp();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState<Monitor | null>(null);
  const [monitorToDelete, setMonitorToDelete] = useState<Monitor | null>(null);

  const handleEdit = (monitor: Monitor) => {
    setSelectedMonitor(monitor);
    setEditModalVisible(true);
  };

  const handleDelete = (monitor: Monitor) => {
    setMonitorToDelete(monitor);
  };

  const handleDeleteConfirm = async () => {
    if (!monitorToDelete) return;
    try {
      await deleteMonitorMutation.mutateAsync(monitorToDelete.id);
      message.success(t("monitorsConfig.deleteSuccess"));
      setMonitorToDelete(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("monitorsConfig.deleteError"));
      throw error;
    }
  };

  const deleteUser = monitorToDelete
    ? users.find((u) => u.id === monitorToDelete.userId)
    : null;
  const deleteUsername = monitorToDelete
    ? deleteUser?.username || `ID ${monitorToDelete.userId}`
    : "";

  const handleEditSubmit = async (
    monitorId: number,
    values: MonitorFormValues,
  ) => {
    try {
      await updateMonitorMutation.mutateAsync({ id: monitorId, data: values });
      message.success(t("monitorsConfig.editSuccess"));
      setEditModalVisible(false);
      setSelectedMonitor(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("monitorsConfig.editError"));
      throw error;
    }
  };

  const isShpickerTable = variant === "shpicker";
  // При редакция от тази таблица се предлагат само потребители от същия вид.
  const selectableUsers = users.filter(
    (user) => !!user.shpicker === isShpickerTable,
  );

  // Панелите са неутрални; разликата се подчертава ДИСКРЕТНО с цвят само на
  // значката, брояча, hover-а на редовете и активната сортировка: монитори —
  // леко оранжево, шпикери — лекото синьо на „Издадено". CSS скоупът е per
  // вариант — двата панела са на една страница.
  const panelScope = isShpickerTable ? "mon-list-shpicker" : "mon-list-monitor";
  const accent = isShpickerTable ? PANEL_ACCENTS.blue : PANEL_ACCENTS.amber;

  const columns: ColumnsType<Monitor> = [
    {
      title: isShpickerTable
        ? t("monitorsConfig.shpickers")
        : t("monitorsConfig.monitors"),
      dataIndex: "userId",
      key: "userId",
      width: 180,
      render: (userId: number) => {
        const user = users.find((u) => u.id === userId);
        return (
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: R.ink,
              letterSpacing: -0.1,
            }}
          >
            {user?.username || `ID ${userId}`}
          </span>
        );
      },
    },
    {
      title: t("monitorsConfig.stations"),
      dataIndex: "stationIds",
      key: "stationIds",
      // При разделен екран показваме двете половини поотделно (Л/Д),
      // иначе — списъка станции на целия екран.
      render: (stationIds: number[], record: Monitor) =>
        record.splitScreen ? (
          <div className="flex flex-col gap-[6px]">
            <StationChips
              stationIds={record.leftStationIds ?? []}
              stations={stations}
              sideLabel={t("monitorsConfig.leftHalfShort")}
            />
            <StationChips
              stationIds={record.rightStationIds ?? []}
              stations={stations}
              sideLabel={t("monitorsConfig.rightHalfShort")}
            />
          </div>
        ) : (
          <StationChips stationIds={stationIds} stations={stations} />
        ),
    },
    {
      title: t("monitorsConfig.viewShort"),
      dataIndex: "view",
      key: "view",
      width: 130,
      align: "center",
      sorter: (a, b) => (a.view ?? "grid").localeCompare(b.view ?? "grid"),
      render: (value: MonitorView | undefined) => {
        const v: MonitorView = value ?? "grid";
        const isGrid = v === "grid";
        return (
          <span
            title={
              isGrid
                ? t("monitorsConfig.viewGrid")
                : t("monitorsConfig.viewList")
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 10px",
              background: accent.tint,
              color: accent.deep,
              border: `1px solid ${accent.tintBorder}`,
              borderRadius: 999,
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: 0.2,
            }}
          >
            {isGrid ? (
              <AppstoreOutlined style={{ fontSize: 11 }} />
            ) : (
              <UnorderedListOutlined style={{ fontSize: 11 }} />
            )}
            {isGrid
              ? t("monitorsConfig.viewGrid")
              : t("monitorsConfig.viewList")}
          </span>
        );
      },
    },
    {
      title: t("monitorsConfig.aggregatedShort"),
      dataIndex: "aggregatedQuantities",
      key: "aggregatedQuantities",
      width: 120,
      align: "center",
      sorter: (a, b) =>
        Number(a.aggregatedQuantities) - Number(b.aggregatedQuantities),
      render: (value: boolean) => (
        <BoolBadge
          value={value}
          title={t("monitorsConfig.aggregatedQuantities")}
          accent={accent}
        />
      ),
    },
    {
      title: t("monitorsConfig.completeFullQuantityShort"),
      dataIndex: "completeFullQuantity",
      key: "completeFullQuantity",
      width: 170,
      align: "center",
      sorter: (a, b) =>
        Number(a.completeFullQuantity) - Number(b.completeFullQuantity),
      render: (value: boolean) => (
        <BoolBadge
          value={value}
          title={t("monitorsConfig.completeFullQuantity")}
          accent={accent}
        />
      ),
    },
    {
      title: t("monitorsConfig.colorByShort"),
      dataIndex: "colorBy",
      key: "colorBy",
      width: 120,
      align: "center",
      render: (value: MonitorColorBy | undefined) => {
        const colorBy: MonitorColorBy = value ?? "table";
        const label =
          colorBy === "waiter"
            ? t("monitorsConfig.colorByWaiter")
            : colorBy === "none"
              ? t("monitorsConfig.colorByNone")
              : t("monitorsConfig.colorByTable");
        return (
          <span className="inline-flex items-center rounded-full border border-shade-2 bg-canvas px-[10px] py-[3px] text-[11.5px] font-semibold tracking-[0.2px] text-body">
            {label}
          </span>
        );
      },
    },
    {
      title: t("monitorsConfig.actions"),
      key: "actions",
      width: 120,
      align: "right",
      render: (_: unknown, record: Monitor) => (
        <div
          style={{
            display: "inline-flex",
            gap: 6,
          }}
        >
          <PanelIconAction
            onClick={() => handleEdit(record)}
            label={t("common.edit", "Edit")}
            accent={accent}
          >
            <EditOutlined />
          </PanelIconAction>
          <PanelIconAction
            onClick={() => handleDelete(record)}
            label={t("common.delete", "Delete")}
            danger
            accent={accent}
          >
            <DeleteOutlined />
          </PanelIconAction>
        </div>
      ),
    },
  ];

  const totalCount = monitors.length;

  return (
    <>
      <ConfigPanel
        scope={panelScope}
        accent={accent}
        icon={
          isShpickerTable ? <ThunderboltFilled /> : <FundProjectionScreenOutlined />
        }
        kicker={
          isShpickerTable
            ? t("monitorsConfig.listKickerShpickers")
            : t("monitorsConfig.listKicker")
        }
        title={
          isShpickerTable
            ? t("monitorsConfig.shpickers")
            : t("monitorsConfig.monitors")
        }
        count={totalCount}
        isNarrow={isNarrow}
        isEmpty={monitors.length === 0}
        emptyText={
          isShpickerTable
            ? t("monitorsConfig.noShpickers")
            : t("monitorsConfig.noMonitors")
        }
        toolbar={
          onAddMonitor ? (
            <PanelAddButton
              onClick={onAddMonitor}
              label={
                isShpickerTable
                  ? t("monitorsConfig.addShpicker")
                  : t("monitorsConfig.addMonitor")
              }
              accent={accent}
              isNarrow={isNarrow}
              dataAttr="data-monitors-add-button"
            />
          ) : undefined
        }
      >
        <Table
          columns={columns}
          dataSource={monitors}
          rowKey="id"
          pagination={false}
          scroll={{ x: 840 }}
          size="small"
        />
      </ConfigPanel>

      {selectedMonitor && (
        <MonitorFormModal
          open={editModalVisible}
          monitor={selectedMonitor}
          variant={variant}
          users={selectableUsers}
          stations={stations}
          onSubmit={(values) => handleEditSubmit(selectedMonitor.id, values)}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedMonitor(null);
          }}
        />
      )}

      <DeleteConfirmModal
        open={!!monitorToDelete}
        title={t("monitorsConfig.deleteConfirmTitle")}
        description={t("monitorsConfig.deleteConfirmContent", {
          name: deleteUsername,
        })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setMonitorToDelete(null)}
      />
    </>
  );
};

// Чипове със станции; при разделен екран отпред стои кръгъл етикет Л/Д.
function StationChips({
  stationIds,
  stations,
  sideLabel,
}: {
  stationIds: number[];
  stations: Station[];
  sideLabel?: string;
}) {
  const { i18n } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {sideLabel && (
        <span className="inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border border-shade-3 bg-canvas text-[10px] font-bold text-body">
          {sideLabel}
        </span>
      )}
      {stationIds.map((stationId) => {
        const station = stations.find((s) => s.id === stationId);
        const stationName = station
          ? getTranslation(station.translations, i18n.language, station.slug)
          : `ID ${stationId}`;
        return (
          <span
            key={stationId}
            className="inline-flex items-center gap-[6px] rounded-[6px] border border-shade-3 bg-surface px-[9px] py-[3px] text-[11.5px] font-semibold tracking-[0.2px] text-ink-2"
          >
            {station?.color && (
              <span
                className="h-[7px] w-[7px] shrink-0 rounded-full"
                style={{ background: station.color }}
              />
            )}
            {stationName}
          </span>
        );
      })}
    </div>
  );
}

function BoolBadge({
  value,
  title,
  accent,
}: {
  value: boolean;
  title: string;
  accent: PanelAccent;
}) {
  return value ? (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: 999,
        background: accent.tint,
        color: accent.deep,
        border: `1px solid ${accent.tintBorder}`,
      }}
    >
      <CheckOutlined style={{ fontSize: 11 }} />
    </span>
  ) : (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: 999,
        background: R.bg,
        color: R.textMuted,
        border: `1px solid ${R.border}`,
      }}
    >
      <CloseOutlined style={{ fontSize: 10 }} />
    </span>
  );
}

export default MonitorsTable;
